import React from "react";
import { motion } from "framer-motion";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import MobileDownloadModal from "components/MobileDownloadModal";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import BackgroundDesktop from "assets/img-winaz/tlmn-1-landing/bglad1ngang.webp";
import BackgroundMobile from "assets/img-winaz/tlmn-1-landing/bglad1doc.webp";
import BgBottom from "assets/img-winaz/live-landing/bgbottom.webp";
import LogoLive from "assets/img-winaz/tlmn-1-landing/logowwinaz.webp";
import LogoHeader from "assets/img-winaz/tlmn-1-landing/txt.webp";
import LogoText2 from "assets/img-winaz/tlmn-1-landing/txt2.webp";
import TypoXocLive from "assets/img-winaz/tlmn-1-landing/ld1headertlmn.webp";
import Label1 from "assets/img-winaz/live-landing/label1.webp";
import Label2 from "assets/img-winaz/live-landing/label2.webp";
import Label3 from "assets/img-winaz/live-landing/label3.webp";
import Label29k from "assets/img-winaz/live-landing/label29k.webp";
import KhungInput from "assets/img-winaz/live-landing/khunginput.webp";
import IconUser from "assets/img-winaz/live-landing/iconuser.webp";
import IconLock from "assets/img-winaz/live-landing/iconlock.webp";
import ButtonRegister from "assets/img-winaz/live-landing/btndangky.webp";
import ButtonDownload from "assets/img-winaz/live-landing/btntaigame.webp";
import ButtonFacebook from "assets/img-winaz/live-landing/btnfacebook.webp";
import ButtonTelegram from "assets/img-winaz/live-landing/btntelegram.webp";
import SideBanner from "assets/img-winaz/tlmn-1-landing/bannergametlmn.webp";
import FloatDecor from "assets/img-winaz/live-landing/float.webp";
import LeafDecor from "assets/img-winaz/live-landing/leaf.webp";
import BannerDemo from "assets/img-winaz/tlmn-1-landing/mainbanerld1.webp";
import IconJackpot from "assets/img-winaz/live-landing/iconjp.webp";
import IconCup from "assets/img-winaz/live-landing/iconcup.webp";
import IconGift from "assets/img-winaz/live-landing/icongifft.webp";
import IconOnline from "assets/img-winaz/live-landing/iconusser.webp";
import IconMoney from "assets/img-winaz/live-landing/icontien.webp";
import IconLiveBadge from "assets/img-winaz/live-landing/iconlive.webp";

import { useLiveStats, formatWin } from "./useLiveStats";
import { SOCIAL_LINKS } from "const";
import { flushEngagementThen } from "utils/dataLayer";

type Platform = "android" | "ios" | "unknown";

const detectPlatform = (userAgent: string): Platform => {
  if (/android/i.test(userAgent)) {
    return "android";
  }
  if (/iPhone|iPad|iPod|iOS/i.test(userAgent)) {
    return "ios";
  }
  return "unknown";
};

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

type SocialLink = {
  id: string;
  icon: string;
  alt: string;
  url: string;
  target?: "_self" | "_blank";
};

const SOCIAL_ITEMS: SocialLink[] = [
  {
    id: "facebook",
    icon: ButtonFacebook,
    alt: "Facebook",
    url: SOCIAL_LINKS.facebook,
    target: "_blank",
  },
  {
    id: "telegram",
    icon: ButtonTelegram,
    alt: "Telegram",
    url: SOCIAL_LINKS.telegram,
    target: "_blank",
  },
];

const INTERCOM_APP_ID = "snx821c4";

type IntercomModule = typeof import("@intercom/messenger-js-sdk");

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const Home: React.FunctionComponent = () => {
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [isMobileDownloadModalOpen, setIsMobileDownloadModalOpen] =
    React.useState(false);
  const [mobilePlatform, setMobilePlatform] = React.useState<"android" | "ios">(
    "android",
  );
  const liveStats = useLiveStats();
  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null,
  );
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);
  const usernameInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);

  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...payload,
      });
    },
    [],
  );

  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) {
      return;
    }

    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);

  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.resolve(null);
    }

    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({
            app_id: INTERCOM_APP_ID,
          });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }

    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleNavigate = React.useCallback(
    (url: string, target: "_self" | "_blank" = "_self") => {
      if (typeof window === "undefined") {
        return;
      }

      if (target === "_blank") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      void flushEngagementThen(url);
    },
    [],
  );

  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
    usernameInputRef.current?.focus();
  }, [pushDataLayerEvent]);

  const handleCloseCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(false);
  }, []);

  const handleOpenDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  const handleCloseDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  const handleOpenMobileDownloadModal = React.useCallback(
    (platform: "android" | "ios") => {
      setMobilePlatform(platform);
      setIsMobileDownloadModalOpen(true);
    },
    [],
  );

  const handleCloseMobileDownloadModal = React.useCallback(() => {
    setIsMobileDownloadModalOpen(false);
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const userAgent = navigator.userAgent;
    const platform = detectPlatform(userAgent);

    pushDataLayerEvent("lp_click_cta_download", {
      platform: platform !== "unknown" ? platform : "android/ios",
      section: "bottom",
    });

    if (platform === "android") {
      handleOpenMobileDownloadModal("android");
    } else if (platform === "ios") {
      handleOpenMobileDownloadModal("ios");
    } else {
      handleOpenDownloadModal();
    }
  }, [
    handleOpenDownloadModal,
    handleOpenMobileDownloadModal,
    pushDataLayerEvent,
  ]);

  const handleSocialClick = React.useCallback(
    (
      platformId: string,
      url: string,
      target: "_self" | "_blank" = "_self",
      location: "hero" | "desktop" | "footer",
    ) => {
      const eventName =
        platformId === "telegram"
          ? "lp_click_social_Telegram"
          : "lp_click_social_FB";
      pushDataLayerEvent(eventName, {
        social_platform: platformId,
        location,
      });
      handleNavigate(url, target);
    },
    [handleNavigate, pushDataLayerEvent],
  );

  const statsCards = [
    {
      id: "jackpot",
      icon: IconJackpot,
      label: "Jackpot đang tích lũy",
      value: formatWin(liveStats.jackpot),
      valueClass: styles.statValueYellow,
      live: false,
    },
    {
      id: "winner",
      icon: IconCup,
      label: (
        <>
          <strong>{liveStats.winnerName}</strong> vừa thắng
        </>
      ),
      value: formatWin(liveStats.winnerPrize),
      valueClass: styles.statValueYellow,
      live: true,
    },
    {
      id: "code",
      icon: IconGift,
      label: (
        <>
          <strong>{liveStats.codeName}</strong> vừa nhận code
        </>
      ),
      value: `+${formatWin(liveStats.codePrize)}`,
      valueClass: styles.statValueGreen,
      live: false,
    },
    {
      id: "online",
      icon: IconOnline,
      label: "Đang chơi",
      value: `${liveStats.online.toLocaleString("vi-VN")} người online`,
      valueClass: styles.statValueYellow,
      live: false,
    },
    {
      id: "paid",
      icon: IconMoney,
      label: "Đã chi thưởng hôm nay",
      value: formatWin(liveStats.paidToday),
      valueClass: styles.statValueYellow,
      live: false,
    },
  ];

  return (
    <div className={styles.home}>
      <img
        className={styles.heroBackgroundDesktop}
        src={BackgroundDesktop}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.heroBackgroundMobile}
        src={BackgroundMobile}
        alt=""
        aria-hidden="true"
      />

      {/* bgbottom phải nằm ngoài .canvas: canvas có transform nên position fixed
          bên trong sẽ bám theo canvas thay vì viewport */}
      <img
        src={BgBottom}
        alt=""
        aria-hidden="true"
        className={styles.bottomPanel}
      />
      <p className={styles.footerText}>
        Chơi có trách nhiệm · Dành cho người 18+ · © WinAZ
      </p>

      <div className={styles.canvas}>

        {/* === Header === */}
        <button
          type="button"
          id="logo"
          className={styles.logoLive}
          onClick={handleLogoClick}
          aria-label="WinAZ Live"
        >
          <img src={LogoLive} alt="WinAZ Live" />
        </button>
        <img
          src={LogoHeader}
          alt="WinAZ TLMN"
          className={styles.logoHeader}
        />
        <img
          src={LogoText2}
          alt="WinAZ TLMN"
          className={styles.logoText2}
        />

        {/* === Khung livestream (ảnh demo, chưa gắn live thật) === */}
        <div className={styles.livePlaceholder} id="live-placeholder">
          <img src={BannerDemo} alt="WinAZ livestream xóc đĩa" />
        </div>

        {/* === Typo + labels === */}
        <img
          src={TypoXocLive}
          alt="TLMN"
          className={styles.typoXocLive}
        />

        {/* === Form đăng ký === */}
        <form className={styles.loginCard} onSubmit={handleInlineSubmit}>
          <p className={styles.formTitle}>ĐĂNG KÝ TÀI KHOẢN</p>
          <div className={styles.loginFields}>
            <div className={styles.fieldGroup}>
              <label
                className={styles.inputWrapper}
                style={{ backgroundImage: `url(${KhungInput})` }}
              >
                <img src={IconUser} alt="" aria-hidden="true" />
                <input
                  ref={usernameInputRef}
                  type="text"
                  placeholder="Tên đăng nhập"
                  value={username}
                  onChange={handleUsernameChange}
                  onFocus={trackInlineFormStart}
                  disabled={isSubmitting}
                />
              </label>
              {usernameError ? (
                <div className={styles.inlineError} aria-live="assertive">
                  Vui lòng nhập tên đăng nhập
                </div>
              ) : null}
            </div>
            <div className={styles.fieldGroup}>
              <label
                className={styles.inputWrapper}
                style={{ backgroundImage: `url(${KhungInput})` }}
              >
                <img src={IconLock} alt="" aria-hidden="true" />
                <input
                  type="password"
                  placeholder="Mật khẩu"
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={trackInlineFormStart}
                  disabled={isSubmitting}
                />
              </label>
              {passwordError ? (
                <div className={styles.inlineError} aria-live="assertive">
                  Vui lòng nhập mật khẩu
                </div>
              ) : null}
            </div>
            {turnstileRequired ? (
              <div className={styles.turnstileGroup}>
                <div className={styles.turnstileBlock}>
                  <div className={styles.turnstileInner}>
                    <TurnstileWidget
                      ref={turnstileWidgetRef}
                      className={styles.turnstileWidget}
                      onTokenChange={handleTurnstileTokenChange}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            {apiError ? (
              <div className={styles.inlineError} aria-live="assertive">
                {apiError}
              </div>
            ) : null}
          </div>

          <motion.button
            type="submit"
            className={styles.registerButtonWrapper}
            disabled={isSubmitting}
            {...INTERACTIVE_IMAGE_PROPS}
            data-cta
          >
            <img
              src={Label29k}
              alt="+29k"
              className={styles.registerLabel}
            />
            <img
              src={ButtonRegister}
              alt="Đăng ký"
              className={styles.registerButtonImage}
            />
          </motion.button>
        </form>

        {/* === Hàng nút cuối trang === */}
        <div className={styles.bottomActions}>
          <motion.button
            type="button"
            id="btn-download"
            className={styles.bottomButton}
            onClick={handleDownloadClick}
            {...INTERACTIVE_IMAGE_PROPS}
          >
            <img src={ButtonDownload} alt="Tải game" />
          </motion.button>
          {SOCIAL_ITEMS.map((item) => (
            <motion.button
              key={item.id}
              id={`social-${item.id}`}
              type="button"
              className={styles.bottomButton}
              onClick={() =>
                handleSocialClick(
                  item.id,
                  item.url,
                  item.target ?? "_self",
                  "footer",
                )
              }
              {...INTERACTIVE_IMAGE_PROPS}
            >
              <img src={item.icon} alt={item.alt} />
            </motion.button>
          ))}
        </div>

        {/* === Banner trái (chỉ landscape) === */}
        <div className={styles.sideBanner}>
          <img src={SideBanner} alt="WinAZ ra mắt Xóc Đĩa - Chơi ngay" />
        </div>

        {/* === Cột thống kê phải (chỉ landscape) === */}
        <div className={styles.statsColumn}>
          {statsCards.map((card) => (
            <div key={card.id} className={styles.statCard}>
              <img className={styles.statIcon} src={card.icon} alt="" aria-hidden="true" />
              <div className={styles.statText}>
                <p className={styles.statLabel}>{card.label}</p>
                <p className={`${styles.statValue} ${card.valueClass}`}>
                  {card.value}
                </p>
              </div>
              {card.live ? (
                <img
                  className={styles.statLiveBadge}
                  src={IconLiveBadge}
                  alt="LIVE"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
      />

      <MobileDownloadModal
        isOpen={isMobileDownloadModalOpen}
        onClose={handleCloseMobileDownloadModal}
        platform={mobilePlatform}
      />

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={handleCloseCreateAccount}
      />
    </div>
  );
};

export default Home;
